var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { computed, observable, reaction } from "mobx";
import { FormModel } from "../FormModel";
import { PatientImportMethod, PatientNotificationType } from "healthex-common/study.dtos";
import { validators } from "../../Logic/Validators";
import { NamedRoutes } from "../NamedRoutes";
import { ContactPreferenceType } from "healthex-common/recruitment.dtos";
import { StudyTranslationLanguage } from "healthex-common/study-translation-language.dtos";
import { SimpleDialogModel } from "../SimpleDialogModel";
import { NOT_LOADED } from "../../Infra/RemoteData";
export class AddPatientsViewModel {
    constructor(opts) {
        this.disposers = [];
        this.editPatientsDialogModel = new SimpleDialogModel();
        this.deletePatientConfirmationDialogModel = new SimpleDialogModel();
        this.addPatientsConfirmationDialogModel = new SimpleDialogModel();
        this.discardChangesConfirmationDialogModel = new SimpleDialogModel();
        this.isShowingSendNotificationsConfirmation = false;
        this.isPreviewDialogOpen = false;
        this.previewData = null;
        this.isLoadingPreview = false;
        this.patients = [];
        this.isValidatingDuplicates = false;
        this.isProcessingCSV = false;
        this.hasProcessedCSV = false;
        this.isSubmitting = false;
        this.editingPatientIndex = null;
        this.gridRefreshTrigger = 0;
        this.formModel = new FormModel({
            lastName: "",
            firstName: "",
            externalId: "",
            dateOfBirth: "",
            email: "",
            phone: "",
            contactPreference: "",
            languagePreference: StudyTranslationLanguage.en,
        }, ["lastName", "firstName", "email", "phone", "contactPreference"]);
        this.editFormModel = new FormModel({
            lastName: "",
            firstName: "",
            externalId: "",
            dateOfBirth: "",
            email: "",
            phone: "",
            contactPreference: "",
            languagePreference: StudyTranslationLanguage.en,
        }, ["lastName", "firstName", "email", "phone", "contactPreference"]);
        this.addPatientCSVFileFormModel = new FormModel({
            file: null,
        }, ["file"]);
        this.addPatientCSVUploadingInfo = null;
        this.updateContactPreference = (preference) => {
            if (!this.shouldAutoSelectContactPreference) {
                this.formModel.input.contactPreference = preference;
                this.formModel.clearError("contactPreference");
            }
        };
        this.updateLanguagePreference = (language) => {
            this.formModel.input.languagePreference = language;
        };
        this.navigateToProjects = () => {
            if (!this.projectId) {
                console.error("No projectId found in route parameters");
                return;
            }
            this.routingUIManager.navigateTo(NamedRoutes.PROJECTS_PROJECT_MANAGER_PATIENT_MANAGEMENT, {
                projectId: this.projectId,
            });
        };
        this.updateEditContactPreference = (preference) => {
            this.editFormModel.updateInput({ ...this.editFormModel.input, contactPreference: preference });
        };
        this.updateEditLanguagePreference = (language) => {
            this.editFormModel.updateInput({ ...this.editFormModel.input, languagePreference: language });
        };
        this.deletePatient = async () => {
            if (this.editingPatientIndex !== null) {
                const confirmed = await this.deletePatientConfirmationDialogModel.showAndWait();
                if (!confirmed)
                    return;
                const wasEditDialogOpen = this.editPatientsDialogModel.open;
                this.patients.splice(this.editingPatientIndex, 1);
                this.gridRefreshTrigger++;
                this.bannerUIManager.displayBanner({ text: "Patient successfully removed" });
                if (wasEditDialogOpen) {
                    await this.closeEditPatientsDialog();
                }
                else {
                    this.editingPatientIndex = null;
                }
            }
        };
        this.deletePatientFromGrid = async (patientIndex) => {
            this.editingPatientIndex = patientIndex;
            const confirmed = await this.deletePatientConfirmationDialogModel.showAndWait();
            if (!confirmed)
                return;
            this.patients.splice(this.editingPatientIndex, 1);
            this.gridRefreshTrigger++;
            this.bannerUIManager.displayBanner({ text: "Patient successfully removed" });
            this.editingPatientIndex = null;
        };
        this.previewNotification = async (notificationType = PatientNotificationType.ENROLLMENT_REQUESTED) => {
            if (!this.projectId || !this.organizationId) {
                console.error("Missing projectId or organizationId");
                return null;
            }
            try {
                this.isLoadingPreview = true;
                this.isPreviewDialogOpen = true;
                this.previewData = null;
                const previewRequest = {
                    projectId: this.projectId,
                    patientNotificationType: notificationType,
                    language: StudyTranslationLanguage.en,
                };
                const preview = await this.patientService.previewNotification(this.projectId, this.organizationId, previewRequest);
                this.previewData = preview;
                return preview;
            }
            catch (error) {
                console.error("Failed to load notification preview:", error);
                this.bannerUIManager.displayBanner({ text: "Failed to load notification preview. Please try again." });
                this.closePreviewDialog();
                throw error;
            }
            finally {
                this.isLoadingPreview = false;
            }
        };
        this.closePreviewDialog = () => {
            this.isPreviewDialogOpen = false;
            this.previewData = null;
            this.isLoadingPreview = false;
        };
        this.addPatientsUIManager = opts.container.addPatientsUIManager;
        this.studiesUIManager = opts.container.studiesUIManager;
        this.bannerUIManager = opts.container.bannerUIManager;
        this.patientService = opts.container.patientService;
        this.authManager = opts.container.authManager;
        this.routingUIManager = opts.container.routingUIManager;
        this.studiesUIManager = opts.container.studiesUIManager;
        this.routingUIManager.registerUnloadHandler({
            needed: () => this.hasUnsavedData,
            confirmation: () => this.showDiscardChangesConfirmationDialog(),
        });
    }
    run() {
        this.disposers.push(this.createContactPreferenceReaction(() => this.availableContactMethods, () => this.shouldAutoSelectContactPreference, () => this.autoSelectedContactPreference, () => this.formModel.input.contactPreference, (preference) => {
            this.formModel.input.contactPreference = preference;
        }, () => this.formModel.clearError("contactPreference")));
        this.disposers.push(this.createContactPreferenceReaction(() => this.editAvailableContactMethods, () => this.shouldEditAutoSelectContactPreference, () => this.editAutoSelectedContactPreference, () => this.editFormModel.input.contactPreference, (preference) => {
            this.editFormModel.updateInput({ ...this.editFormModel.input, contactPreference: preference });
        }, () => this.editFormModel.clearError("contactPreference")));
        this.disposers.push(reaction(() => ({
            email: this.formModel.input.email,
            phone: this.formModel.input.phone,
            lastName: this.formModel.input.lastName,
            firstName: this.formModel.input.firstName,
            contactPreference: this.formModel.input.contactPreference,
        }), ({ email, phone, lastName, firstName, contactPreference }) => {
            if (lastName.trim().length > 0) {
                this.formModel.clearError("lastName");
            }
            if (firstName.trim().length > 0) {
                this.formModel.clearError("firstName");
            }
            if (email.trim().length > 0 && validators.validEmail(email)) {
                this.formModel.clearError("email");
            }
            if (phone.trim().length > 0 && validators.validPhone(phone)) {
                this.formModel.clearError("phone");
            }
            if (contactPreference !== "" && this.availableContactMethods.includes(contactPreference)) {
                this.formModel.clearError("contactPreference");
            }
        }));
    }
    get studyToManage() {
        return this.studiesUIManager.studyToManage;
    }
    createContactPreferenceReaction(getAvailableMethods, getShouldAutoSelect, getAutoSelected, getCurrentSelection, setPreference, clearError) {
        return reaction(() => ({
            availableMethods: getAvailableMethods(),
            shouldAutoSelect: getShouldAutoSelect(),
            autoSelected: getAutoSelected(),
            currentSelection: getCurrentSelection(),
        }), ({ availableMethods, shouldAutoSelect, autoSelected, currentSelection }) => {
            if (availableMethods.length === 0) {
                if (currentSelection !== "") {
                    setPreference("");
                }
            }
            else if (shouldAutoSelect && autoSelected !== "") {
                if (currentSelection !== autoSelected) {
                    setPreference(autoSelected);
                    clearError();
                }
            }
            else if (availableMethods.length > 1) {
                if (currentSelection !== "" && !availableMethods.includes(currentSelection)) {
                    setPreference("");
                }
            }
        }, { fireImmediately: true });
    }
    stop() {
        this.disposers.forEach((disposer) => disposer());
        this.disposers = [];
        this.routingUIManager.clearUnloadConfirmation();
    }
    get projectId() {
        return this.addPatientsUIManager.projectId;
    }
    get method() {
        return this.addPatientsUIManager.method;
    }
    get isManualMethod() {
        return this.method === PatientImportMethod.MANUAL;
    }
    get isFileMethod() {
        return this.method === PatientImportMethod.FILE;
    }
    get areNotificationsSuppressed() {
        if (this.studyToManage === NOT_LOADED || this.studyToManage === null) {
            return false;
        }
        return this.studyToManage.suppressNotifications;
    }
    get organizationId() {
        return this.authManager.orgId;
    }
    get pageTitle() {
        if (this.method === PatientImportMethod.FILE) {
            return "Add patients from a file";
        }
        return "Add patients manually";
    }
    get hasPatients() {
        return this.patients.length > 0;
    }
    get hasValidPatientsToSubmit() {
        return this.successfulPatients.length > 0;
    }
    get canSubmitPatients() {
        return this.hasValidPatientsToSubmit && !this.isSubmitting;
    }
    get successfulPatients() {
        return this.patients.filter((p) => p.status === "success");
    }
    get reviewPatients() {
        return this.patients.filter((p) => p.status === "error");
    }
    get duplicatePatients() {
        return this.patients.filter((p) => p.status === "duplicate");
    }
    get showStatusColumn() {
        return this.isFileMethod && this.hasPatients && this.patients.some((p) => p.status);
    }
    get hasEmail() {
        return this.formModel.input.email.trim().length > 0;
    }
    get hasPhone() {
        return this.formModel.input.phone.trim().length > 0;
    }
    get availableContactMethods() {
        const methods = [];
        if (this.hasEmail && validators.validEmail(this.formModel.input.email)) {
            methods.push(ContactPreferenceType.EMAIL);
        }
        if (this.hasPhone && validators.validPhone(this.formModel.input.phone)) {
            methods.push(ContactPreferenceType.PHONE);
        }
        return methods;
    }
    get shouldAutoSelectContactPreference() {
        return this.availableContactMethods.length === 1;
    }
    get autoSelectedContactPreference() {
        if (this.shouldAutoSelectContactPreference) {
            return this.availableContactMethods[0];
        }
        return "";
    }
    get effectiveContactPreference() {
        if (this.shouldAutoSelectContactPreference) {
            return this.autoSelectedContactPreference;
        }
        return this.formModel.input.contactPreference;
    }
    get isFormValid() {
        if (this.formModel.hasErrors()) {
            return false;
        }
        const hasRequiredFields = this.formModel.input.lastName.trim().length > 0 && this.formModel.input.firstName.trim().length > 0;
        const hasValidEmail = this.hasEmail && validators.validEmail(this.formModel.input.email);
        const hasValidPhone = this.hasPhone && validators.validPhone(this.formModel.input.phone);
        const hasValidContactInfo = hasValidEmail || hasValidPhone;
        const needsContactPreferenceSelection = this.availableContactMethods.length > 1;
        const hasContactPreferenceSelected = this.formModel.input.contactPreference !== "";
        const contactPreferenceValid = !needsContactPreferenceSelection || hasContactPreferenceSelected;
        return hasRequiredFields && hasValidContactInfo && contactPreferenceValid && !this.isValidatingDuplicates;
    }
    get hasEditEmail() {
        return this.editFormModel.input.email.trim().length > 0;
    }
    get hasEditPhone() {
        return this.editFormModel.input.phone.trim().length > 0;
    }
    get editAvailableContactMethods() {
        const methods = [];
        if (this.hasEditEmail && validators.validEmail(this.editFormModel.input.email)) {
            methods.push("email");
        }
        if (this.hasEditPhone && validators.validPhone(this.editFormModel.input.phone)) {
            methods.push("phone");
        }
        return methods;
    }
    get shouldEditAutoSelectContactPreference() {
        return this.editAvailableContactMethods.length === 1;
    }
    get editAutoSelectedContactPreference() {
        if (this.shouldEditAutoSelectContactPreference) {
            return this.editAvailableContactMethods[0];
        }
        return "";
    }
    get editEffectiveContactPreference() {
        if (this.shouldEditAutoSelectContactPreference) {
            return this.editAutoSelectedContactPreference;
        }
        return this.editFormModel.input.contactPreference;
    }
    get isEditFormValid() {
        if (this.editFormModel.hasErrors()) {
            return false;
        }
        const hasRequiredFields = this.editFormModel.input.lastName.trim().length > 0 && this.editFormModel.input.firstName.trim().length > 0;
        const hasValidEmail = this.hasEditEmail && validators.validEmail(this.editFormModel.input.email);
        const hasValidPhone = this.hasEditPhone && validators.validPhone(this.editFormModel.input.phone);
        const hasValidContactInfo = hasValidEmail || hasValidPhone;
        const needsContactPreferenceSelection = this.editAvailableContactMethods.length > 1;
        const hasContactPreferenceSelected = this.editFormModel.input.contactPreference !== "";
        const contactPreferenceValid = !needsContactPreferenceSelection || hasContactPreferenceSelected;
        return hasRequiredFields && hasValidContactInfo && contactPreferenceValid;
    }
    get shouldShowConfirmation() {
        return this.isFileMethod && this.hasProcessedCSV && (this.reviewPatients.length > 0 || this.duplicatePatients.length > 0);
    }
    get hasUnsavedData() {
        const manualFormHasData = this.formModel.input.lastName.trim() !== "" ||
            this.formModel.input.firstName.trim() !== "" ||
            this.formModel.input.externalId.trim() !== "" ||
            this.formModel.input.dateOfBirth.trim() !== "" ||
            this.formModel.input.email.trim() !== "" ||
            this.formModel.input.phone.trim() !== "";
        const hasPatients = this.patients.length > 0;
        const hasUploadedFile = this.addPatientCSVFileFormModel.input.file !== null;
        return manualFormHasData || hasPatients || hasUploadedFile;
    }
    isValidEmail(email) {
        return validators.validEmail(email);
    }
    isValidPhone(phone) {
        return validators.validPhone(phone);
    }
    async showDiscardChangesConfirmationDialog() {
        return await this.discardChangesConfirmationDialogModel.showAndWait();
    }
    validateContactMethods() {
        const { validEmail, validPhone } = validators;
        const f = this.formModel;
        const input = f.input;
        const hasEmailInput = input.email.trim().length > 0;
        const hasPhoneInput = input.phone.trim().length > 0;
        f.clearError("email", "phone");
        if (!hasEmailInput && !hasPhoneInput) {
            f.addError("email", "Either email or phone number is required");
            f.addError("phone", "Either email or phone number is required");
            return false;
        }
        if (hasEmailInput) {
            f.validateField("email", validEmail, "Please enter a valid email address");
        }
        if (hasPhoneInput) {
            f.validateField("phone", validPhone, "Please enter a valid phone number");
        }
        return !f.hasError("email", "phone");
    }
    async validateDuplicates() {
        var _a;
        const f = this.formModel;
        const input = f.input;
        const hasValidEmail = input.email.trim().length > 0 && validators.validEmail(input.email);
        const hasValidPhone = input.phone.trim().length > 0 && validators.validPhone(input.phone);
        if (!hasValidEmail && !hasValidPhone) {
            return true;
        }
        this.isValidatingDuplicates = true;
        try {
            const duplicateCheck = await this.patientService.checkForDuplicates(this.projectId, this.organizationId, [
                { email: hasValidEmail ? input.email : undefined, phone: hasValidPhone ? input.phone : undefined },
            ]);
            const result = (_a = duplicateCheck[0]) !== null && _a !== void 0 ? _a : { email: undefined, phone: undefined, emailExists: false, phoneExists: false };
            if (result.emailExists && hasValidEmail) {
                f.addError("email", "A patient with this email address already exists. Please check the email or use a different one.");
            }
            if (result.phoneExists && hasValidPhone) {
                f.addError("phone", "A patient with this phone number already exists. Please check the number or use a different one.");
            }
            return !result.emailExists && !result.phoneExists;
        }
        catch (error) {
            console.warn("Error validating duplicates:", error);
            return true;
        }
        finally {
            this.isValidatingDuplicates = false;
        }
    }
    async validateForm() {
        const { notBlank } = validators;
        const f = this.formModel;
        f.clearErrors();
        f.validateField("lastName", notBlank, "Last name is required");
        f.validateField("firstName", notBlank, "First name is required");
        const contactMethodsValid = this.validateContactMethods();
        if (contactMethodsValid) {
            if (this.availableContactMethods.length > 1 && !this.formModel.input.contactPreference) {
                f.addError("contactPreference", "Please select a contact preference");
            }
        }
        if (contactMethodsValid && !f.hasErrors()) {
            await this.validateDuplicates();
        }
        return !f.hasErrors();
    }
    async addPatient() {
        const isValid = await this.validateForm();
        if (!isValid) {
            return false;
        }
        const newPatient = {
            id: crypto.randomUUID(),
            lastName: this.formModel.input.lastName.trim(),
            firstName: this.formModel.input.firstName.trim(),
            externalId: this.formModel.input.externalId.trim() || "",
            dateOfBirth: this.formModel.input.dateOfBirth.trim() || "",
            email: this.formModel.input.email.trim(),
            phone: this.formModel.input.phone.trim(),
            contactPreference: this.effectiveContactPreference,
            languagePreference: this.formModel.input.languagePreference,
            status: "success",
            missingFields: [],
        };
        this.patients.unshift(newPatient);
        this.bannerUIManager.displayBanner({ text: "Patient successfully added for review", type: "SUCCESS" });
        this.formModel.reset();
        this.formModel.input.languagePreference = StudyTranslationLanguage.en;
        return true;
    }
    removePatient(index) {
        this.patients.splice(index, 1);
    }
    capitalizeFirstLetter(str) {
        return str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "";
    }
    formatContactPreference(preference) {
        return preference ? this.capitalizeFirstLetter(preference) : "";
    }
    formatLanguagePreference(language) {
        switch (language) {
            case StudyTranslationLanguage.en:
                return "English";
            case StudyTranslationLanguage.es:
                return "Spanish";
            default:
                return "English";
        }
    }
    formatPatientName(patient) {
        return `${this.capitalizeFirstLetter(patient.lastName)}, ${this.capitalizeFirstLetter(patient.firstName)}`;
    }
    downloadPatientFileTemplate() {
        const headers = ["lastName", "firstName", "externalId", "dateOfBirth", "email", "phone", "contactPreference", "languagePreference"];
        const csvData = [headers];
        let csvContent = "";
        csvData.forEach((row) => {
            csvContent += `${row.join(",")}\n`;
        });
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8," });
        const objUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", objUrl);
        link.setAttribute("download", `patient_import_template.csv`);
        link.textContent = "Download patient template";
        link.click();
        URL.revokeObjectURL(objUrl);
    }
    async handleCSVFileSelected(file) {
        this.addPatientCSVFileFormModel.input.file = file;
        this.addPatientCSVFileFormModel.clearErrors();
        const isValid = await this.validateCSVFormat(file);
        if (isValid) {
            this.isProcessingCSV = true;
            try {
                await this.processCSVFile(file);
            }
            finally {
                this.isProcessingCSV = false;
            }
        }
    }
    async validateCSVFormat(file) {
        try {
            const text = await file.text();
            const lines = text.trim().split("\n");
            if (lines.length === 0) {
                this.addPatientCSVFileFormModel.addError("file", "The CSV file is empty");
                return false;
            }
            const expectedHeaders = [
                "lastName",
                "firstName",
                "externalId",
                "dateOfBirth",
                "email",
                "phone",
                "contactPreference",
                "languagePreference",
            ];
            const actualHeaders = lines[0].split(",").map((header) => header.trim());
            const headersMatch = expectedHeaders.length === actualHeaders.length && expectedHeaders.every((header, index) => header === actualHeaders[index]);
            if (!headersMatch) {
                this.addPatientCSVFileFormModel.addError("file", `This file doesn't match our required format. Please use the provided template above.`);
                return false;
            }
            if (lines.length < 2) {
                this.addPatientCSVFileFormModel.addError("file", "CSV file must contain at least one patient record");
                return false;
            }
            for (let i = 1; i < lines.length; i++) {
                const row = lines[i].split(",");
                if (row.length !== expectedHeaders.length) {
                    this.addPatientCSVFileFormModel.addError("file", `Row ${i + 1} has ${row.length} columns, expected ${expectedHeaders.length}`);
                    return false;
                }
            }
            return true;
        }
        catch (error) {
            this.addPatientCSVFileFormModel.addError("file", "Error reading CSV file. Please ensure it's a valid CSV format.");
            return false;
        }
    }
    async processCSVFile(file) {
        var _a, _b;
        try {
            const text = await file.text();
            const lines = text.trim().split("\n");
            const dataRows = lines.slice(1);
            const allPatients = [];
            for (const rowData of dataRows) {
                const row = rowData.split(",").map((cell) => cell.trim());
                const patient = {
                    id: crypto.randomUUID(),
                    lastName: row[0] || "",
                    firstName: row[1] || "",
                    externalId: row[2] || "",
                    dateOfBirth: row[3] || "",
                    email: row[4] || "",
                    phone: row[5] || "",
                    contactPreference: (_a = row[6]) !== null && _a !== void 0 ? _a : "",
                    languagePreference: (_b = row[7]) !== null && _b !== void 0 ? _b : StudyTranslationLanguage.en,
                };
                allPatients.push(patient);
            }
            for (const patient of allPatients) {
                this.validatePatientFields(patient);
            }
            await this.validateDuplicatesForAllPatients(allPatients);
            this.patients = allPatients;
            this.hasProcessedCSV = true;
        }
        catch (error) {
            this.addPatientCSVFileFormModel.addError("file", "Error processing CSV file. Please try again.");
        }
    }
    validatePatientFields(patient) {
        const missingFields = [];
        if (!patient.firstName && !patient.lastName) {
            missingFields.push("name");
        }
        const hasValidEmail = patient.email && validators.validEmail(patient.email);
        const hasValidPhone = patient.phone && validators.validPhone(patient.phone);
        if (!hasValidEmail && !hasValidPhone) {
            if (!patient.email && !patient.phone) {
                missingFields.push("contact");
            }
            else {
                if (patient.email && !validators.validEmail(patient.email)) {
                    missingFields.push("valid email");
                }
                if (patient.phone && !validators.validPhone(patient.phone)) {
                    missingFields.push("valid phone");
                }
            }
        }
        if (patient.email !== "" || patient.phone !== "") {
            if (!patient.contactPreference) {
                missingFields.push("contact preference");
            }
            else {
                if (patient.contactPreference === "email" && !hasValidEmail) {
                    missingFields.push("contact preference");
                }
                else if (patient.contactPreference === "phone" && !hasValidPhone) {
                    missingFields.push("contact preference");
                }
            }
        }
        if (!patient.languagePreference ||
            (patient.languagePreference !== StudyTranslationLanguage.en && patient.languagePreference !== StudyTranslationLanguage.es)) {
            missingFields.push("language preference");
        }
        if (missingFields.length > 0) {
            patient.status = "error";
            patient.missingFields = missingFields;
        }
        else {
            patient.status = "success";
        }
    }
    async validateDuplicatesForAllPatients(patients) {
        if (!this.projectId || !this.organizationId) {
            return;
        }
        const emailMap = new Map();
        const phoneMap = new Map();
        patients.forEach((patient, index) => {
            var _a, _b;
            if (patient.email) {
                const email = patient.email.toLowerCase();
                const indices = (_a = emailMap.get(email)) !== null && _a !== void 0 ? _a : [];
                indices.push(index);
                emailMap.set(email, indices);
            }
            if (patient.phone) {
                const indices = (_b = phoneMap.get(patient.phone)) !== null && _b !== void 0 ? _b : [];
                indices.push(index);
                phoneMap.set(patient.phone, indices);
            }
        });
        const duplicateIndices = new Set();
        emailMap.forEach((indices) => {
            if (indices.length > 1) {
                indices.forEach((index) => duplicateIndices.add(index));
            }
        });
        phoneMap.forEach((indices) => {
            if (indices.length > 1) {
                indices.forEach((index) => duplicateIndices.add(index));
            }
        });
        duplicateIndices.forEach((index) => {
            patients[index].status = "duplicate";
        });
        const patientsToCheck = patients.filter((p) => p.status !== "duplicate" && p.status !== "error");
        if (patientsToCheck.length === 0) {
            return;
        }
        try {
            const batchData = patientsToCheck.map((patient) => ({
                email: patient.email,
                phone: patient.phone,
            }));
            const uniqueBatchData = [];
            const patientToUniqueIndex = [];
            batchData.forEach((data, index) => {
                const key = `${data.email || ""}|${data.phone || ""}`;
                let uniqueIndex = uniqueBatchData.findIndex((unique) => { var _a, _b; return `${(_a = unique.email) !== null && _a !== void 0 ? _a : ""}|${(_b = unique.phone) !== null && _b !== void 0 ? _b : ""}` === key; });
                if (uniqueIndex === -1) {
                    uniqueIndex = uniqueBatchData.length;
                    uniqueBatchData.push(data);
                }
                patientToUniqueIndex[index] = uniqueIndex;
            });
            const batchSize = 100;
            const allDuplicateResults = [];
            for (let i = 0; i < uniqueBatchData.length; i += batchSize) {
                const batch = uniqueBatchData.slice(i, i + batchSize);
                const batchResults = await this.patientService.checkForDuplicates(this.projectId, this.organizationId, batch);
                allDuplicateResults.push(...batchResults);
                if (i + batchSize < uniqueBatchData.length) {
                    await new Promise((resolve) => setTimeout(resolve, 10));
                }
            }
            for (let i = 0; i < patientsToCheck.length; i++) {
                const patient = patientsToCheck[i];
                const uniqueIndex = patientToUniqueIndex[i];
                const result = allDuplicateResults[uniqueIndex];
                if (result.emailExists || result.phoneExists) {
                    patient.status = "duplicate";
                }
            }
        }
        catch (error) {
            console.warn("Batch duplicate checking failed, assuming no duplicates:", error);
        }
    }
    async addPatients() {
        if (this.shouldShowConfirmation) {
            this.isShowingSendNotificationsConfirmation = !this.areNotificationsSuppressed;
            const confirmed = await this.addPatientsConfirmationDialogModel.showAndWait();
            if (!confirmed)
                return;
        }
        await this.performAddPatients();
    }
    async performAddPatients() {
        if (!this.projectId || !this.organizationId)
            return;
        const validPatients = this.patients.filter((p) => p.status === "success");
        if (validPatients.length === 0)
            return;
        this.isSubmitting = true;
        try {
            const apiPatients = validPatients.map((patient) => ({
                lastName: patient.lastName,
                firstName: patient.firstName,
                externalId: patient.externalId,
                dateOfBirth: patient.dateOfBirth ? new Date(patient.dateOfBirth) : undefined,
                email: patient.email,
                phone: patient.phone,
                contactPreference: patient.contactPreference,
                languagePreference: patient.languagePreference,
            }));
            const input = {
                projectId: this.projectId,
                patients: apiPatients,
            };
            const result = await this.patientService.addPatients(this.projectId, input);
            const clientErrorCount = this.reviewPatients.length;
            const clientDuplicateCount = this.duplicatePatients.length;
            if (result.successCount > 0) {
                this.patients.splice(0, this.patients.length, ...this.patients.filter((p) => p.status !== "success"));
                let bannerMessage = `${result.successCount} new patient${result.successCount === 1 ? "" : "s"} added`;
                const details = [];
                if (clientErrorCount > 0) {
                    details.push(`${clientErrorCount} weren't added due to missing required information`);
                }
                if (clientDuplicateCount > 0) {
                    details.push(`${clientDuplicateCount} duplicate${clientDuplicateCount === 1 ? " was" : "s were"} skipped`);
                }
                if (result.errorCount > 0) {
                    details.push(`${result.errorCount} had server-side errors`);
                }
                if (result.duplicatesCount > 0) {
                    details.push(`${result.duplicatesCount} additional duplicate${result.duplicatesCount === 1 ? " was" : "s were"} detected`);
                }
                if (details.length > 0) {
                    bannerMessage += `, ${details.join(", ")}`;
                }
                if (this.patients.length === 0) {
                    this.formModel.reset();
                    this.formModel.input.languagePreference = StudyTranslationLanguage.en;
                    this.addPatientCSVFileFormModel.reset();
                }
                this.routingUIManager.navigateTo(NamedRoutes.PROJECTS_PROJECT_MANAGER_PATIENT_MANAGEMENT, {
                    projectId: this.projectId,
                });
                this.bannerUIManager.displayBanner({ text: bannerMessage, type: "SUCCESS" });
            }
        }
        catch (error) {
            console.error("Error adding patients:", error);
        }
        finally {
            this.isSubmitting = false;
        }
    }
    showEditPatientsDialog(patientIndex) {
        this.editingPatientIndex = patientIndex;
        const patient = this.patients[patientIndex];
        if (patient) {
            this.editFormModel.updateInput({
                lastName: patient.lastName,
                firstName: patient.firstName,
                externalId: patient.externalId,
                dateOfBirth: patient.dateOfBirth,
                email: patient.email,
                phone: patient.phone,
                contactPreference: patient.contactPreference,
                languagePreference: patient.languagePreference,
            });
        }
        this.editPatientsDialogModel.show();
    }
    async closeEditPatientsDialog() {
        this.editFormModel.reset();
        this.editPatientsDialogModel.open = false;
        this.editingPatientIndex = null;
    }
    async saveEditedPatient() {
        if (this.editingPatientIndex !== null && this.isEditFormValid) {
            const updatedPatient = {
                ...this.patients[this.editingPatientIndex],
                lastName: this.editFormModel.input.lastName,
                firstName: this.editFormModel.input.firstName,
                externalId: this.editFormModel.input.externalId,
                dateOfBirth: this.editFormModel.input.dateOfBirth,
                email: this.editFormModel.input.email,
                phone: this.editFormModel.input.phone,
                contactPreference: this.editFormModel.input.contactPreference,
                languagePreference: this.editFormModel.input.languagePreference,
            };
            this.validatePatientFields(updatedPatient);
            this.patients.splice(this.editingPatientIndex, 1, updatedPatient);
            await this.validateDuplicatesForAllPatients(this.patients);
            this.gridRefreshTrigger++;
            this.bannerUIManager.displayBanner({ text: "Edits successfully saved" });
            this.editFormModel.reset();
            this.editingPatientIndex = null;
            this.editPatientsDialogModel.open = false;
        }
    }
}
__decorate([
    observable,
    __metadata("design:type", Boolean)
], AddPatientsViewModel.prototype, "isShowingSendNotificationsConfirmation", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], AddPatientsViewModel.prototype, "isPreviewDialogOpen", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AddPatientsViewModel.prototype, "previewData", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], AddPatientsViewModel.prototype, "isLoadingPreview", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientsViewModel.prototype, "studyToManage", null);
__decorate([
    observable,
    __metadata("design:type", Array)
], AddPatientsViewModel.prototype, "patients", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AddPatientsViewModel.prototype, "isValidatingDuplicates", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AddPatientsViewModel.prototype, "isProcessingCSV", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AddPatientsViewModel.prototype, "hasProcessedCSV", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AddPatientsViewModel.prototype, "isSubmitting", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AddPatientsViewModel.prototype, "editingPatientIndex", void 0);
__decorate([
    observable,
    __metadata("design:type", Number)
], AddPatientsViewModel.prototype, "gridRefreshTrigger", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AddPatientsViewModel.prototype, "addPatientCSVUploadingInfo", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientsViewModel.prototype, "projectId", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientsViewModel.prototype, "method", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientsViewModel.prototype, "isManualMethod", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientsViewModel.prototype, "isFileMethod", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientsViewModel.prototype, "areNotificationsSuppressed", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientsViewModel.prototype, "organizationId", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientsViewModel.prototype, "pageTitle", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientsViewModel.prototype, "hasPatients", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientsViewModel.prototype, "hasValidPatientsToSubmit", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientsViewModel.prototype, "canSubmitPatients", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientsViewModel.prototype, "successfulPatients", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientsViewModel.prototype, "reviewPatients", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientsViewModel.prototype, "duplicatePatients", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientsViewModel.prototype, "showStatusColumn", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientsViewModel.prototype, "hasEmail", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientsViewModel.prototype, "hasPhone", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientsViewModel.prototype, "availableContactMethods", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientsViewModel.prototype, "shouldAutoSelectContactPreference", null);
__decorate([
    computed,
    __metadata("design:type", String),
    __metadata("design:paramtypes", [])
], AddPatientsViewModel.prototype, "autoSelectedContactPreference", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientsViewModel.prototype, "effectiveContactPreference", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientsViewModel.prototype, "isFormValid", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientsViewModel.prototype, "hasEditEmail", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientsViewModel.prototype, "hasEditPhone", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientsViewModel.prototype, "editAvailableContactMethods", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientsViewModel.prototype, "shouldEditAutoSelectContactPreference", null);
__decorate([
    computed,
    __metadata("design:type", String),
    __metadata("design:paramtypes", [])
], AddPatientsViewModel.prototype, "editAutoSelectedContactPreference", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientsViewModel.prototype, "editEffectiveContactPreference", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientsViewModel.prototype, "isEditFormValid", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientsViewModel.prototype, "shouldShowConfirmation", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientsViewModel.prototype, "hasUnsavedData", null);
